import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { DataState } from '@/components/DataState';
import { Save, Webhook, Zap, Phone, Plus, Pencil, Trash2, UserCheck, UserX } from 'lucide-react';

export default function SettingsPage() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ twilio_enabled: true, auto_reply: true, auto_process: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const isAdmin = user?.role === 'admin';

  // Approved senders state
  const [senders, setSenders] = useState([]);
  const [sendersLoading, setSendersLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editSender, setEditSender] = useState(null);
  const [senderForm, setSenderForm] = useState({ phone_number: '', name: '' });
  const [senderSaving, setSenderSaving] = useState(false);

  useEffect(() => {
    api.get('/settings')
      .then(res => setSettings(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    fetchSenders();
  }, []);

  const fetchSenders = async () => {
    setSendersLoading(true);
    try {
      const { data } = await api.get('/approved-senders');
      setSenders(data);
    } catch (err) { void 0; }
    setSendersLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings', {
        twilio_enabled: settings.twilio_enabled,
        auto_reply: settings.auto_reply,
        auto_process: settings.auto_process,
      });
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleAddSender = async () => {
    if (!senderForm.phone_number.trim()) {
      toast.error('Phone number is required');
      return;
    }
    setSenderSaving(true);
    try {
      await api.post('/approved-senders', senderForm);
      toast.success('Sender added');
      setShowAddDialog(false);
      setSenderForm({ phone_number: '', name: '' });
      fetchSenders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add sender');
    }
    setSenderSaving(false);
  };

  const handleEditSender = async () => {
    setSenderSaving(true);
    try {
      await api.put(`/approved-senders/${editSender.sender_id}`, {
        phone_number: senderForm.phone_number,
        name: senderForm.name,
      });
      toast.success('Sender updated');
      setEditSender(null);
      setSenderForm({ phone_number: '', name: '' });
      fetchSenders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
    setSenderSaving(false);
  };

  const handleToggleSender = async (sender) => {
    try {
      await api.put(`/approved-senders/${sender.sender_id}`, { is_active: !sender.is_active });
      toast.success(sender.is_active ? 'Sender deactivated' : 'Sender activated');
      fetchSenders();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDeleteSender = async (senderId) => {
    if (!window.confirm('Remove this approved sender?')) return;
    try {
      await api.delete(`/approved-senders/${senderId}`);
      toast.success('Sender removed');
      fetchSenders();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const openEditDialog = (sender) => {
    setEditSender(sender);
    setSenderForm({ phone_number: sender.phone_number, name: sender.name || '' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const webhookUrl = `${window.location.origin}/api/webhook/whatsapp`;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Configure your automation preferences</p>
      </div>

      {/* Webhook URL */}
      <div className="border border-border rounded-md p-6 space-y-4" data-testid="webhook-section">
        <div className="flex items-center gap-2 mb-2">
          <Webhook className="w-4 h-4 text-[hsl(var(--accent))]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">WhatsApp Webhook URL</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Configure this URL in your Twilio WhatsApp sandbox or number settings as the webhook endpoint.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-secondary px-4 py-2.5 rounded-[4px] text-sm font-mono break-all" data-testid="webhook-url">
            {webhookUrl}
          </code>
          <Button
            variant="outline"
            size="sm"
            className="rounded-[4px] shrink-0"
            onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success('Copied!'); }}
            data-testid="copy-webhook-btn"
          >
            Copy
          </Button>
        </div>
      </div>

      {/* Approved Senders */}
      <div className="border border-border rounded-md p-6 space-y-4" data-testid="approved-senders-section">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[hsl(var(--accent))]" />
            <h3 className="text-sm font-semibold uppercase tracking-wider">Approved Senders</h3>
          </div>
          {isAdmin && (
            <Button
              size="sm"
              variant="outline"
              className="rounded-[4px]"
              onClick={() => { setSenderForm({ phone_number: '', name: '' }); setShowAddDialog(true); }}
              data-testid="add-sender-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Number
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Only messages from these WhatsApp numbers will be processed. Numbers must include country code (e.g. +60123456789).
        </p>

        <DataState
          loading={sendersLoading}
          empty={senders.length === 0}
          emptyContent={
            <div className="py-8 text-center border border-dashed border-border rounded-md" data-testid="no-senders">
              <Phone className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No approved senders yet.</p>
              <p className="text-xs text-muted-foreground mt-1">Add WhatsApp numbers to start processing receipts.</p>
            </div>
          }
        >
          <div className="divide-y divide-border border border-border rounded-md overflow-hidden" data-testid="senders-list">
            {senders.map((s) => (
              <div
                key={s.sender_id}
                className={`flex items-center justify-between px-4 py-3 transition-colors ${
                  s.is_active ? 'hover:bg-secondary/30' : 'opacity-50 bg-secondary/20'
                }`}
                data-testid={`sender-row-${s.sender_id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    s.is_active ? 'bg-[hsl(var(--success))]/20 text-[hsl(var(--success))]' : 'bg-secondary text-muted-foreground'
                  }`}>
                    {s.is_active ? <UserCheck className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-mono font-medium">{s.phone_number}</p>
                    {s.name && <p className="text-xs text-muted-foreground truncate">{s.name}</p>}
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0 ml-3">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-7 h-7"
                      onClick={() => handleToggleSender(s)}
                      data-testid={`toggle-sender-${s.sender_id}`}
                      title={s.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {s.is_active ? <UserX className="w-3.5 h-3.5" /> : <UserCheck className="w-3.5 h-3.5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-7 h-7"
                      onClick={() => openEditDialog(s)}
                      data-testid={`edit-sender-${s.sender_id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="w-7 h-7 text-destructive hover:text-destructive"
                      onClick={() => handleDeleteSender(s.sender_id)}
                      data-testid={`delete-sender-${s.sender_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DataState>
      </div>

      {/* Automation Settings */}
      <div className="border border-border rounded-md p-6 space-y-6" data-testid="automation-section">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-4 h-4 text-[hsl(var(--accent))]" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">Automation</h3>
        </div>

        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Auto-Process Receipts</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Automatically extract data from incoming receipts using AI</p>
            </div>
            <Switch
              checked={settings.auto_process}
              onCheckedChange={(v) => setSettings(s => ({ ...s, auto_process: v }))}
              disabled={!isAdmin}
              data-testid="auto-process-toggle"
            />
          </div>

          <div className="border-t border-border" />

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Auto-Reply on WhatsApp</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Send receipt summary back to WhatsApp after processing</p>
            </div>
            <Switch
              checked={settings.auto_reply}
              onCheckedChange={(v) => setSettings(s => ({ ...s, auto_reply: v }))}
              disabled={!isAdmin}
              data-testid="auto-reply-toggle"
            />
          </div>

          <div className="border-t border-border" />

          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Twilio Integration</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Enable WhatsApp message receiving via Twilio</p>
            </div>
            <Switch
              checked={settings.twilio_enabled}
              onCheckedChange={(v) => setSettings(s => ({ ...s, twilio_enabled: v }))}
              disabled={!isAdmin}
              data-testid="twilio-toggle"
            />
          </div>
        </div>

        {isAdmin && (
          <Button onClick={handleSave} disabled={saving} className="rounded-[4px]" data-testid="save-settings-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        )}

        {!isAdmin && (
          <p className="text-xs text-muted-foreground border border-border rounded-[4px] px-3 py-2 bg-secondary/50">
            Only admins can modify these settings.
          </p>
        )}
      </div>

      {/* Add Sender Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => !open && setShowAddDialog(false)}>
        <DialogContent className="sm:max-w-md" data-testid="add-sender-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Add Approved Sender</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground">Phone Number</Label>
              <Input
                value={senderForm.phone_number}
                onChange={(e) => setSenderForm(f => ({ ...f, phone_number: e.target.value }))}
                placeholder="+60123456789"
                className="rounded-[4px] font-mono"
                data-testid="sender-phone-input"
              />
              <p className="text-xs text-muted-foreground">Include country code with + prefix</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground">Name (Optional)</Label>
              <Input
                value={senderForm.name}
                onChange={(e) => setSenderForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. John - Purchasing"
                className="rounded-[4px]"
                data-testid="sender-name-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)} className="rounded-[4px]" data-testid="add-sender-cancel-btn">Cancel</Button>
              <Button onClick={handleAddSender} disabled={senderSaving} className="rounded-[4px]" data-testid="add-sender-save-btn">
                {senderSaving ? 'Adding...' : 'Add Sender'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Sender Dialog */}
      <Dialog open={!!editSender} onOpenChange={(open) => !open && setEditSender(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-sender-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Edit Approved Sender</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground">Phone Number</Label>
              <Input
                value={senderForm.phone_number}
                onChange={(e) => setSenderForm(f => ({ ...f, phone_number: e.target.value }))}
                placeholder="+60123456789"
                className="rounded-[4px] font-mono"
                data-testid="edit-sender-phone-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-muted-foreground">Name (Optional)</Label>
              <Input
                value={senderForm.name}
                onChange={(e) => setSenderForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. John - Purchasing"
                className="rounded-[4px]"
                data-testid="edit-sender-name-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditSender(null)} className="rounded-[4px]" data-testid="edit-sender-cancel-btn">Cancel</Button>
              <Button onClick={handleEditSender} disabled={senderSaving} className="rounded-[4px]" data-testid="edit-sender-save-btn">
                {senderSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
